import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import Button from 'components/Button';
import Message from 'components/Message';
import { closeModal } from 'components/Modal';
import { ICONS } from 'constants/icons';
import appMessages from 'features/App/App.messages';
import { bem } from 'helpers/class-name';

import messages from '../SkuMenuPage.messages';
import './SkuRemoveItemFromCategoryModal.scss';

const { block, element } = bem('sku-remove-item-from-category-modal');

export const SKU_REMOVE_ITEM_FROM_CATEGORY_MODAL_NAME = 'SkuRemoveItemFromCategoryModal';

function SkuRemoveItemFromCategoryModal({
  itemName,
  categoryName,
  categoryCount,
  onRemoveFromCategory,
  onRemoveFromAllMenus,
}) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [isRemoving, setIsRemoving] = useState(false);

  const handleCancel = () => {
    if (!isRemoving) {
      dispatch(closeModal());
    }
  };

  const handleAction = async action => {
    if (isRemoving) {
      return;
    }

    setIsRemoving(true);
    try {
      await action?.();
      dispatch(closeModal());
    } catch (error) {
      // Caller handles user feedback; keep modal open for retry.
    } finally {
      setIsRemoving(false);
    }
  };

  const isMultiCategory = categoryCount > 1;
  const title = isMultiCategory ? messages.removeItemFromCategoryTitle : messages.removeItemTitle;
  const titleValues = isMultiCategory
    ? { itemName, categoryName, strong: chunks => <strong>{chunks}</strong> }
    : { itemName, strong: chunks => <strong>{chunks}</strong> };
  const description = isMultiCategory ? messages.removeItemFromCategoryDescription : messages.removeItemDescription;

  return (
    <div
      className={block()}
      role="dialog"
      aria-modal="true"
      aria-label={intl.formatMessage(messages.removeItemDialogLabel)}>
      <header className={element('header')}>
        <h3 className={element('title')}>
          <Message message={title} values={titleValues} />
        </h3>
        <button
          type="button"
          aria-label={intl.formatMessage(appMessages.close)}
          className={element('close-icon')}
          onClick={handleCancel}>
          <Isvg src={ICONS.cross} />
        </button>
      </header>
      <p className={element('description')}>
        <Message message={description} />
      </p>
      <footer className={element('footer')}>
        <Message
          as={Button}
          className={element('action-btn')}
          styled="primary"
          type={Button.TYPES.BUTTON}
          disabled={isRemoving}
          waiting={isRemoving}
          onClick={() => handleAction(onRemoveFromCategory)}
          message={isMultiCategory ? messages.removeFromThisCategory : messages.removeItemConfirmAction}
        />
        {isMultiCategory && (
          <Message
            as={Button}
            className={element('action-btn')}
            styled="primary-outline"
            type={Button.TYPES.BUTTON}
            disabled={isRemoving}
            waiting={isRemoving}
            onClick={() => handleAction(onRemoveFromAllMenus)}
            message={messages.removeFromAllMenus}
          />
        )}
        <button className={element('cancel-btn')} type="button" disabled={isRemoving} onClick={handleCancel}>
          <Message message={messages.removeItemCancel} />
        </button>
      </footer>
    </div>
  );
}

SkuRemoveItemFromCategoryModal.propTypes = {
  itemName: PropTypes.string.isRequired,
  categoryName: PropTypes.string,
  categoryCount: PropTypes.number.isRequired,
  onRemoveFromCategory: PropTypes.func.isRequired,
  onRemoveFromAllMenus: PropTypes.func.isRequired,
};

export default SkuRemoveItemFromCategoryModal;
